import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCategory } from '../context/CategoryContext';
import { useMonth } from '../context/MonthContext';
import { useMetaData } from '../hooks/useApi';
import { buildViewHref } from '../utils/deepLink';

// ── Category icons ────────────────────────────────────────────────────────────
// Matches on the leaf name (part after "/" if present)
function CatIcon({ category, size = 18 }) {
  const name = category?.includes(' / ') ? category.split(' / ').pop().trim() : category;
  const s = { width: size, height: size };
  if (name === 'Ride Hailing') return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M5 17H3a2 2 0 01-2-2V9l3-5h11l3 5v6a2 2 0 01-2 2h-2"/>
      <circle cx="7.5" cy="17.5" r="2.5"/><circle cx="16.5" cy="17.5" r="2.5"/>
    </svg>
  );
  if (name === 'Food Delivery') return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M18 8h1a4 4 0 010 8h-1"/><path d="M2 8h16v9a4 4 0 01-4 4H6a4 4 0 01-4-4V8z"/>
      <line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/>
    </svg>
  );
  if (name === 'Life & Health Insurance') return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      <path d="M12 8v4M10 10h4"/>
    </svg>
  );
  if (name === 'Shopping DIY') return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
    </svg>
  );
  if (name === 'Shopping Grocery') return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
      <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
    </svg>
  );
  if (name === 'Banking' || name === 'Finance') return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="2" y="11" width="20" height="10" rx="2"/><path d="M6 11V7a6 6 0 0112 0v4"/>
      <line x1="12" y1="15" x2="12" y2="17"/>
    </svg>
  );
  if (name === 'Streaming' || name === 'Entertainment') return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
    </svg>
  );
  if (name === 'Travel') return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.09 1.19 2 2 0 012.06 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
    </svg>
  );
  // Fallback
  return (
    <svg {...s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
      <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
    </svg>
  );
}

// ── Group/market icons ─────────────────────────────────────────────────────────
function GroupIcon({ group }) {
  if (group === 'UK') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
      <path d="M4.22 4.22l15.56 15.56M19.78 4.22L4.22 19.78" strokeWidth="0.9" strokeOpacity="0.5"/>
    </svg>
  );
  if (group === 'Thailand' || group === 'TH') return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      <circle cx="12" cy="9" r="2.5"/>
    </svg>
  );
  // Generic market/region icon
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <circle cx="12" cy="12" r="10"/>
      <line x1="2" y1="12" x2="22" y2="12"/>
      <path d="M12 2a15.3 15.3 0 010 20M12 2a15.3 15.3 0 000 20"/>
    </svg>
  );
}

// ── Nav item icons ────────────────────────────────────────────────────────────
const ICONS = {
  chart:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="18" y="3" width="4" height="18"/><rect x="10" y="8" width="4" height="13"/><rect x="2" y="13" width="4" height="8"/></svg>,
  trend:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>,
  people:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>,
  overlap: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="12" r="6"/><circle cx="15" cy="12" r="6"/></svg>,
  target:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  social:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>,
  market:  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>,
  pin:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/></svg>,
  lock:    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></svg>,
  doc:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
};

// ── Navigation definition ─────────────────────────────────────────────────────
const NAV = [
  {
    section: 'Panel Statistics',
    items: [
      { id: 'panel-representativeness', label: 'Panel Representativeness', icon: 'market' },
      { id: 'panel-size',               label: 'Panel Size',              icon: 'people' },
    ],
  },
  {
    section: 'How is the category growing?',
    items: [
      { id: 'category-behaviour', label: 'Category Behaviour', icon: 'market' },
      {
        id: '_category-comparison', label: 'Category Comparison', icon: 'chart', expandable: true,
        children: [
          { id: 'category-comparison-national',  label: 'National' },
          { id: 'category-comparison-provinces', label: 'Provinces' },
        ],
      },
      {
        id: '_penetration', label: 'Category Penetration', icon: 'target', expandable: true,
        children: [
          { id: 'category-hml-profile', label: 'H/M/L Profile' },
          { id: 'penetration-geo',    label: 'By Geo' },
          { id: 'penetration-age',    label: 'By Age Band' },
          { id: 'penetration-gender', label: 'By Gender' },
        ],
      },
      {
        id: '_daily-trends', label: 'Monthly Trends', icon: 'trend', expandable: true,
        children: [
          { id: 'trends-age',        label: 'By Age Band' },
          { id: 'trends-gender',     label: 'By Gender' },
          { id: 'trends-geo',        label: 'Geography' },
          { id: 'trends-intensity',  label: 'Intensity' },
          { id: 'trends-engagement', label: 'Engagement' },
          { id: null,             label: 'SES',           locked: true },
          { id: null,             label: 'New vs Repeat', locked: true },
        ],
      },
    ],
  },
  {
    section: 'How is your brand growing?',
    items: [
      { id: 'double-jeopardy', label: 'Share and Penetration', icon: 'chart' },
      {
        id: '_brand-share', label: 'Brand Share', icon: 'chart', expandable: true,
        children: [
          { id: 'brand-share-national',  label: 'National' },
          { id: 'brand-share-intensity', label: 'H/M/L (Intensity)' },
          { id: 'brand-share-loyalty',   label: 'Solus/Dual (Loyalty)' },
        ],
      },
      { id: 'trends-nation',   label: 'Nation Topline',        icon: 'trend' },
      {
        id: '_brand-penetration', label: 'Brand Penetration', icon: 'target', expandable: true,
        children: [
          { id: 'brand-penetration-geo',    label: 'By Geo' },
          { id: 'brand-penetration-age',    label: 'By Age Band' },
          { id: 'brand-penetration-gender', label: 'By Gender' },
        ],
      },
    ],
  },
  {
    section: 'Who is your competition?',
    items: [
      {
        id: '_customer-profiles', label: 'Customer Profiles', icon: 'people', expandable: true,
        children: [
          { id: 'by-age',    label: 'Demographics — Age' },
          { id: 'by-gender', label: 'Demographics — Gender' },
          { id: 'intensity', label: 'Intensity' },
          { id: 'by-geo',    label: 'Geography' },
          { id: 'loyalty',   label: 'App Loyalty' },
          { id: null,        label: 'Personas',             locked: true },
          { id: null,        label: 'SES',                  locked: true },
          { id: null,        label: 'Telco Pre-Paid vs Post-Paid', locked: true },
          { id: null,        label: 'User Churn',           locked: true },
        ],
      },
      {
        id: '_shared-customers', label: 'Shared Customers', icon: 'overlap', expandable: true,
        children: [
          { id: 'competition', label: 'Duplication Matrix' },
          { id: 'sor',         label: 'Share of Requirements' },
          { id: null,          label: 'Heavy Users Analysis', locked: true },
        ],
      },
    ],
  },
  {
    section: 'What are your Growth Opportunities?',
    items: [
      { id: 'category-target-audience', label: 'Who are the Category Target Audience?', icon: 'people' },
      { id: 'brand-target-audience',    label: 'Who are my Brand Target Audience?',     icon: 'target' },
      { id: 'penetration-targets',      label: 'Penetration Targets',                   icon: 'chart'  },
    ],
  },
  {
    section: 'Identify Channel Preferences',
    items: [
      { id: 'social',             label: 'Social Channel Reach',          icon: 'pin' },
      { id: 'channel-opp-index', label: 'Opportunity Index by Channel',   icon: 'pin' },
      { id: null,                label: 'Where do my target audience visit', locked: true },
    ],
  },
  {
    section: 'Location Intelligence',
    badge: 'NEW',
    items: [
      { id: 'location-map',          label: 'Postcode Intelligence', icon: 'pin' },
      { id: 'pinpoint-intelligence', label: 'Pinpoint Intelligence', icon: 'pin' },
    ],
  },
  {
    section: 'Omni - Cross Category',
    badge: 'NEW',
    items: [
      { id: 'football-affinity',   label: 'Football Affinity',    icon: 'overlap' },
      { id: 'theme-park-affinity', label: 'Theme Parks Affinity', icon: 'pin' },
    ],
  },
  {
    section: 'Media Budget Allocation',
    badge: 'NEW',
    items: [
      { id: 'media-budget-brand',     label: 'Based on Brand Behavior',    icon: 'target' },
      { id: 'media-budget-intensity', label: 'Based on Category Intensity', icon: 'chart'  },
    ],
  },
  {
    section: 'Mobility Intelligence',
    comingSoon: true,
    items: [
      { id: null, label: 'Brand Share of Routes',               locked: true },
      { id: null, label: 'Supply and Demand',                   locked: true },
      { id: null, label: 'Average Distance vs Sessions by Day', locked: true },
      { id: null, label: 'Brand share of Time of Day slots',    locked: true },
      { id: null, label: 'Most Popular Routes',                 locked: true },
      { id: null, label: 'Performance by Category Intensity',   locked: true },
    ],
  },
  {
    section: 'Design your target audience',
    comingSoon: true,
    items: [],
  },
];

// ── Change Password Modal ─────────────────────────────────────────────────────
function ChangePasswordModal({ onClose }) {
  const { completePasswordChange } = useAuth();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw,     setNewPw]     = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [err,       setErr]       = useState('');
  const [busy,      setBusy]      = useState(false);

  const inputSt = {
    width: '100%', marginBottom: 10, padding: '10px 12px', borderRadius: 8,
    border: '1px solid var(--border)', background: 'var(--bg-base)',
    color: 'var(--text-primary)', fontSize: 14, boxSizing: 'border-box',
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentPw)          { setErr('Enter your current password.'); return; }
    if (newPw.length < 8)    { setErr('New password must be at least 8 characters.'); return; }
    if (newPw !== confirmPw) { setErr('Passwords do not match.'); return; }
    setBusy(true); setErr('');
    try {
      await completePasswordChange(currentPw, newPw);
      onClose();
    } catch (ex) {
      const msg = ex.code === 'auth/wrong-password' || ex.code === 'auth/invalid-credential'
        ? 'Current password is incorrect.'
        : ex.message || 'Failed to update password. Please try again.';
      setErr(msg);
      setBusy(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: 'var(--bg-card)', borderRadius: 12, padding: '36px 32px',
        width: 360, boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
        maxHeight: '90vh', overflowY: 'auto',
      }}>
        <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6, color: 'var(--text-primary)' }}>
          Change password
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 24 }}>
          Confirm your current password, then enter a new one.
        </div>
        <form onSubmit={handleSubmit}>
          <input
            type="password" placeholder="Current password" value={currentPw}
            onChange={e => setCurrentPw(e.target.value)} required
            style={inputSt}
          />
          <input
            type="password" placeholder="New password" value={newPw}
            onChange={e => setNewPw(e.target.value)} required
            style={inputSt}
          />
          <input
            type="password" placeholder="Confirm new password" value={confirmPw}
            onChange={e => setConfirmPw(e.target.value)} required
            style={{ ...inputSt, marginBottom: 16 }}
          />
          {err && <div style={{ color: 'var(--color-negative)', fontSize: 13, marginBottom: 12 }}>{err}</div>}
          <button type="submit" disabled={busy} style={{
            width: '100%', padding: '11px', borderRadius: 8, border: 'none',
            background: 'var(--teal)', color: '#fff', fontWeight: 600,
            fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
          }}>
            {busy ? 'Saving…' : 'Change password'}
          </button>
        </form>
        <button onClick={onClose} style={{
          width: '100%', marginTop: 10, padding: '9px', borderRadius: 8,
          border: '1px solid var(--border)', background: 'transparent',
          color: 'var(--text-muted)', fontSize: 13, cursor: 'pointer',
        }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// Helper: find which section name contains a given view id
function findSectionForView(view) {
  for (const sec of NAV) {
    for (const item of sec.items) {
      if (item.id === view) return sec.section;
      if (item.children?.some(c => c.id === view)) return sec.section;
    }
  }
  return null;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function Sidebar({ activeView, onNavigate, onGoHome, country }) {
  const { user, logout, isEurekaUser, isEmailUser, allowedCategories } = useAuth();
  const { category, setCategory }    = useCategory();
  const { month }                    = useMonth();
  const { categories, loading: metaLoading } = useMetaData();

  // Deep-link href for a view id (keeps current market/category/month), so
  // right-click → "Open in new tab" lands on that exact view.
  const viewHref = (id) => buildViewHref({ country, category, month, view: id });

  const [catOpen,      setCatOpen]      = useState(false);
  const [openCatGroup, setOpenCatGroup] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);

  // Expandable items within a section (Monthly Trends, Customer Profiles, etc.)
  const [openItems, setOpenItems] = useState({ '_daily-trends': true, '_customer-profiles': true, '_penetration': true, '_brand-penetration': true, '_brand-share': true, '_category-comparison': true });

  // Which section accordion is open (only one at a time)
  const [openSection, setOpenSection] = useState(() => findSectionForView(activeView));
  // comingSoon sections default open; track which ones the user has manually closed
  const [closedSoonSections, setClosedSoonSections] = useState(new Set(['Mobility Intelligence', 'Design your target audience']));

  const catRef  = useRef(null);
  const userRef = useRef(null);

  const closeOnOutside = useCallback((ref, setter) => (e) => {
    if (ref.current && !ref.current.contains(e.target)) setter(false);
  }, []);

  // Close dropdowns on outside click
  useEffect(() => {
    const closeCat  = closeOnOutside(catRef,  setCatOpen);
    const closeUser = closeOnOutside(userRef, setUserMenuOpen);
    document.addEventListener('mousedown', closeCat);
    document.addEventListener('mousedown', closeUser);
    return () => {
      document.removeEventListener('mousedown', closeCat);
      document.removeEventListener('mousedown', closeUser);
    };
  }, [closeOnOutside]);

  // Auto-open the section that contains the active view
  useEffect(() => {
    const sec = findSectionForView(activeView);
    if (sec) setOpenSection(sec);
  }, [activeView]);

  const toggleSection = (id) =>
    setOpenItems(prev => ({ ...prev, [id]: !prev[id] }));

  const toggleSectionAccordion = (sectionName) =>
    setOpenSection(prev => prev === sectionName ? null : sectionName);

  const handleNavigate = (id) => {
    if (!id) return;
    onNavigate(id);
  };

  // Real category list: strip 'ALL', then apply per-user permission filter if set
  const catList = (categories || [])
    .filter(c => c !== 'ALL')
    .filter(c => !allowedCategories || allowedCategories.includes(c));

  // If the active category is no longer in the allowed list, clear it
  useEffect(() => {
    if (category && catList.length > 0 && !catList.includes(category)) {
      setCategory(null);
    }
  }, [catList, category, setCategory]);

  // Split catList into grouped (name contains " / " with non-empty sub) and direct entries.
  // "Food Delivery /" → direct with display "Food Delivery" (sub is empty, strip the slash)
  const { grouped, direct } = useMemo(() => {
    const grouped = {};
    const direct = [];
    catList.forEach(c => {
      const sep = c.indexOf(' / ');
      if (sep !== -1) {
        const group = c.slice(0, sep).trim();
        const sub   = c.slice(sep + 3).trim();
        if (sub === '') {
          direct.push({ full: c, display: group });
        } else {
          if (!grouped[group]) grouped[group] = [];
          grouped[group].push({ full: c, sub });
        }
      } else {
        direct.push({ full: c, display: c });
      }
    });
    // Sort direct items and groups alphabetically; sort sub-items within each group
    direct.sort((a, b) => a.display.localeCompare(b.display));
    Object.values(grouped).forEach(subs => subs.sort((a, b) => a.sub.localeCompare(b.sub)));
    return { grouped: Object.fromEntries(Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b))), direct };
  }, [catList]);

  // Display helpers: show leaf name and group label separately in the selected-category button.
  // For trailing-slash categories ("Food Delivery /") the leaf is the part before "/".
  const catLeafName = (c) => {
    const sep = c?.indexOf(' / ');
    if (sep === -1 || sep == null) return c;
    const sub = c.slice(sep + 3).trim();
    return sub || c.slice(0, sep).trim();
  };
  // Only return a group label if there's a real sub-category (non-empty after "/")
  const catGroupName = (c) => {
    const sep = c?.indexOf(' / ');
    if (sep == null || sep === -1) return null;
    const sub = c.slice(sep + 3).trim();
    return sub ? c.slice(0, sep).trim() : null;
  };

  return (
    <aside className="sidebar sidebar-new">

      {/* ── Category selector ── */}
      <div className="sb-cat-wrap" ref={catRef}>
        <button
          className={`sb-cat-btn ${catOpen ? 'open' : ''}`}
          onClick={() => {
            const next = !catOpen;
            setCatOpen(next);
            if (next) setOpenCatGroup(catGroupName(category));
          }}
          disabled={metaLoading}
        >
          <div className="sb-cat-icon">
            {category ? <CatIcon category={category} /> : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="16"/><line x1="8" y1="12" x2="16" y2="12"/>
              </svg>
            )}
          </div>
          <div className="sb-cat-text">
            <div className="sb-cat-name">{category ? catLeafName(category) : 'Select a category'}</div>
            <div className="sb-cat-sub">{category ? (catGroupName(category) || 'Pro') : 'No category selected'}</div>
          </div>
          <svg className={`sb-cat-arrow ${catOpen ? 'up' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </button>

        {catOpen && (
          <div className="sb-cat-dropdown">
            <div className="sb-cat-dropdown-label">Select category</div>

            {/* Direct categories — major category level, slash stripped from display */}
            {direct.map(({ full, display }) => (
              <button
                key={full}
                className={`sb-cat-group-btn ${category === full ? 'active' : ''}`}
                onClick={() => { setCategory(full); setCatOpen(false); onNavigate('panel-size'); }}
              >
                <span className="sb-cat-group-icon"><CatIcon category={display} size={15} /></span>
                <span>{display}</span>
              </button>
            ))}

            {/* Grouped categories — accordion by market/region */}
            {Object.entries(grouped).map(([group, subs]) => (
              <div key={group}>
                <button
                  className={`sb-cat-group-btn ${openCatGroup === group ? 'open' : ''}`}
                  onClick={() => setOpenCatGroup(g => g === group ? null : group)}
                >
                  <span className="sb-cat-group-icon"><GroupIcon group={group} /></span>
                  <span className="sb-cat-group-label">{group}</span>
                  <svg
                    className={`sb-cat-group-arrow ${openCatGroup === group ? 'open' : ''}`}
                    width="12" height="12" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" strokeWidth="2.5"
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {openCatGroup === group && (
                  <div className="sb-cat-submenu">
                    {subs.map(({ full, sub }) => (
                      <button
                        key={full}
                        className={`sb-cat-suboption ${category === full ? 'active' : ''}`}
                        onClick={() => { setCategory(full); setCatOpen(false); onNavigate('panel-size'); }}
                      >
                        <span className="sb-cat-suboption-icon"><CatIcon category={sub} size={15} /></span>
                        <span>{sub}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Navigation (only shown when category selected) ── */}
      {category && (
        <div className="sb-nav">
          {NAV.map(section => {
            const hasItems = section.items.length > 0;
            const hasActive = section.items.some(item =>
              item.id === activeView || (item.children || []).some(c => c.id === activeView)
            );
            const isSoon = !!section.comingSoon;
            const isOpen = isSoon
              ? (hasItems && !closedSoonSections.has(section.section))
              : openSection === section.section;

            const toggleSoon = () => setClosedSoonSections(prev => {
              const next = new Set(prev);
              next.has(section.section) ? next.delete(section.section) : next.add(section.section);
              return next;
            });

            return (
              <div key={section.section} className="sb-section">
                {/* Section accordion header */}
                <button
                  className={`sb-section-btn ${isOpen ? 'open' : ''} ${hasActive && !isOpen ? 'has-active' : ''} ${!hasItems ? 'empty' : ''} ${isSoon ? 'coming-soon' : ''}`}
                  onClick={() => isSoon ? (hasItems && toggleSoon()) : (hasItems && toggleSectionAccordion(section.section))}
                  disabled={!hasItems}
                >
                  <span className="sb-section-btn-label">{section.section}</span>
                  {section.badge && <span className="sb-badge-new">{section.badge}</span>}
                  {isSoon && <span className="sb-soon-badge">Soon</span>}
                  {hasItems && (
                    <svg
                      className={`sb-section-arrow ${isOpen ? 'open' : ''}`}
                      width="12" height="12" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2.5"
                    >
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  )}
                </button>

                {/* Expanded items box */}
                {isOpen && hasItems && (
                  <div className="sb-section-body">
                    {section.items.map(item => (
                      <div key={item.id || item.label}>
                        {item.locked ? (
                          <div className="sb-item sb-item-locked">
                            <span className="sb-item-icon">{ICONS.lock}</span>
                            <span>{item.label}</span>
                          </div>
                        ) : item.expandable ? (
                          <>
                            <button
                              className={`sb-item sb-item-parent ${openItems[item.id] ? 'open' : ''}`}
                              onClick={() => toggleSection(item.id)}
                            >
                              <span className="sb-item-icon">{ICONS[item.icon]}</span>
                              <span className="sb-item-label">{item.label}</span>
                              <svg className={`sb-expand-arrow ${openItems[item.id] ? 'down' : ''}`} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                <polyline points="6 9 12 15 18 9"/>
                              </svg>
                            </button>
                            {openItems[item.id] && (
                              <div className="sb-children">
                                {item.children.map(child => (
                                  child.locked ? (
                                    <div key={child.label} className="sb-child sb-child-locked">
                                      <span className="sb-lock">{ICONS.lock}</span>
                                      {child.label}
                                    </div>
                                  ) : (
                                    <a
                                      key={child.id}
                                      href={viewHref(child.id)}
                                      className={`sb-child ${activeView === child.id ? 'active' : ''}`}
                                      onClick={(e) => {
                                        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                                        e.preventDefault(); handleNavigate(child.id);
                                      }}
                                    >
                                      {child.label}
                                    </a>
                                  )
                                ))}
                              </div>
                            )}
                          </>
                        ) : (
                          <a
                            href={viewHref(item.id)}
                            className={`sb-item ${activeView === item.id ? 'active' : ''}`}
                            onClick={(e) => {
                              if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                              e.preventDefault(); handleNavigate(item.id);
                            }}
                          >
                            <span className="sb-item-icon">{ICONS[item.icon]}</span>
                            <span className="sb-item-label">{item.label}</span>
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── Eureka-only pinned links ── */}
      {isEurekaUser && (
        <div className="sb-docs-pin">
          <button
            className={`sb-item ${activeView === 'docs' ? 'active' : ''}`}
            onClick={() => onNavigate('docs')}
          >
            <span className="sb-item-icon">{ICONS.doc}</span>
            <span className="sb-item-label">Documentation</span>
          </button>
          <button
            className={`sb-item ${activeView === 'data-schema' ? 'active' : ''}`}
            onClick={() => onNavigate('data-schema')}
          >
            <span className="sb-item-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3"/>
                <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5"/>
              </svg>
            </span>
            <span className="sb-item-label">Data Schema</span>
          </button>
          <button
            className={`sb-item ${activeView === 'validation' ? 'active' : ''}`}
            onClick={() => onNavigate('validation')}
          >
            <span className="sb-item-icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
            </span>
            <span className="sb-item-label">Data Validation</span>
          </button>
        </div>
      )}

      {changePwOpen && <ChangePasswordModal onClose={() => setChangePwOpen(false)} />}

      {/* ── User card + dropdown ── */}
      <div className="sidebar-footer" ref={userRef}>

        {/* Dropdown menu — appears above the card */}
        {userMenuOpen && (
          <div className="sb-user-menu">
            <div className="sb-user-menu-header">
              <div className="sb-user-menu-name">{user?.displayName || 'User'}</div>
              <div className="sb-user-menu-email">{user?.email}</div>
            </div>

            {onGoHome && (
              <button
                className="sb-user-menu-item"
                onClick={() => { setUserMenuOpen(false); onGoHome(); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
                Back to Home
              </button>
            )}

            {isEmailUser && (
              <button
                className="sb-user-menu-item"
                onClick={() => { setUserMenuOpen(false); setChangePwOpen(true); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2"/>
                  <path d="M7 11V7a5 5 0 0110 0v4"/>
                </svg>
                Change password
              </button>
            )}

            <button
              className="sb-user-menu-item sb-user-menu-signout"
              onClick={() => { setUserMenuOpen(false); logout(); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
              Sign out
            </button>
          </div>
        )}

        <div
          className={`user-card ${userMenuOpen ? 'active' : ''}`}
          onClick={() => setUserMenuOpen(o => !o)}
          style={{ cursor: 'pointer' }}
        >
          {user?.photoURL
            ? <img src={user.photoURL} alt="avatar" className="user-avatar" />
            : <div className="user-avatar" style={{ display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, color:'white' }}>
                {user?.displayName?.[0] || user?.email?.[0]?.toUpperCase() || '?'}
              </div>
          }
          <div style={{ overflow:'hidden', flex:1 }}>
            <div className="user-name">{user?.displayName || 'User'}</div>
            <div className="user-email">{user?.email}</div>
          </div>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
            style={{ flexShrink:0, opacity:0.4, transform: userMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </div>
      </div>
    </aside>
  );
}
